/**
 * Policies and consent, as the API sends them.
 *
 * Every document, version and requirement is created here, in the portal, and
 * stored by the API: nothing about what a client is asked to accept is written
 * into this app or the client apps. Labels for the fixed choices (kinds,
 * channels, purposes, services) come from `/legal/options` for the same reason.
 */

import { format } from 'date-fns';
import { api, type Tone } from '@connect/web-shared';

export interface Option {
  value: string;
  label: string;
}

export interface LegalOptions {
  document_kinds: Option[];
  requirement_kinds: Option[];
  channels: Option[];
  purposes: Option[];
  services: Option[];
}

export interface VersionSummary {
  public_id: string;
  number: number;
  label: string;
  status: 'draft' | 'published';
  change_summary: string | null;
  requires_reconsent: boolean;
  effective_at: string | null;
  published_at: string | null;
  created_at: string;
  is_current: boolean;
  decisions: number;
}

export interface DocumentRow {
  public_id: string;
  key: string;
  kind: string;
  title: string;
  description: string | null;
  service: string | null;
  channel: string | null;
  is_public: boolean;
  status: 'active' | 'retired';
  current_version: VersionSummary | null;
  scheduled_version: VersionSummary | null;
  has_draft: boolean;
  updated_at: string;
}

export interface DocumentDetail extends DocumentRow {
  versions: VersionSummary[];
}

export interface VersionDetail extends VersionSummary {
  document_public_id: string;
  document_key: string;
  document_title: string;
  content: string;
  content_format: string;
  content_sha256: string | null;
}

export interface RequirementRow {
  public_id: string;
  key: string;
  kind: string;
  title: string;
  explanation: string | null;
  statement: string;
  document_public_id: string;
  document_key: string;
  document_title: string;
  mandatory: boolean;
  withdrawable: boolean;
  enforce_on_api: boolean;
  service: string | null;
  channel: string | null;
  purpose: string | null;
  status: 'draft' | 'active' | 'retired';
  effective_at: string | null;
  display_order: number;
  accepted: number;
  updated_at: string;
}

export interface VersionRef {
  public_id: string;
  label: string;
  effective_at: string | null;
  change_summary: string | null;
}

export interface ConsentState {
  requirement_public_id: string;
  key: string;
  kind: string;
  title: string;
  statement: string;
  mandatory: boolean;
  withdrawable: boolean;
  channel: string | null;
  purpose: string | null;
  document_key: string;
  document_title: string;
  status: 'pending' | 'accepted' | 'declined' | 'withdrawn';
  current_version: VersionRef;
  decided_version: VersionRef | null;
  decided_at: string | null;
  updated_since_decision: boolean;
}

export interface ConsentRecordRow {
  public_id: string;
  requirement_public_id: string;
  requirement_title: string;
  decision: 'accepted' | 'declined' | 'withdrawn';
  statement: string;
  document_title: string;
  version_label: string;
  version_public_id: string;
  content_sha256: string;
  platform: string;
  created_at: string;
  login: string | null;
  ip_address: string | null;
  user_agent: string | null;
  request_id: string | null;
  client_public_id: string | null;
  client_name: string | null;
  superseded: boolean;
}

export interface ClientConsents {
  requirements: ConsentState[];
  pending_mandatory: number;
  history: ConsentRecordRow[];
  history_total: number;
}

export const legalKeys = {
  all: ['legal'] as const,
  options: ['legal', 'options'] as const,
  documents: ['legal', 'documents'] as const,
  document: (id: string) => ['legal', 'documents', id] as const,
  version: (id: string) => ['legal', 'versions', id] as const,
  requirements: ['legal', 'requirements'] as const,
  records: ['legal', 'records'] as const,
  client: (id: string) => ['legal', 'clients', id] as const,
};

export const fetchOptions = () => api.get<LegalOptions>('/legal/options');
export const fetchDocuments = () => api.get<DocumentRow[]>('/legal/documents');
export const fetchRequirements = () => api.get<RequirementRow[]>('/legal/requirements');

export const STATUS_TONES: Record<string, Tone> = {
  active: 'success',
  published: 'success',
  draft: 'warning',
  scheduled: 'info',
  retired: 'neutral',
  accepted: 'success',
  pending: 'warning',
  declined: 'danger',
  withdrawn: 'neutral',
};

/** The label for a value, from the server's options; the value itself if unknown. */
export function labelOf(options: Option[] | undefined, value: string | null): string | null {
  if (value === null) return null;
  return options?.find((o) => o.value === value)?.label ?? value;
}

/**
 * A `datetime-local` value (the reader's wall clock) as the instant it names.
 *
 * `new Date('2026-10-01T09:00')` reads a zone-less string as local time, which
 * is exactly what the person picking it meant; `toISOString` then carries the
 * zone to the API, which refuses an effective time without one.
 */
export function localInputToInstant(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/** An instant as a `datetime-local` value on the reader's wall clock. */
export function instantToLocalInput(value: string | null): string {
  if (!value) return '';
  return format(new Date(value), "yyyy-MM-dd'T'HH:mm");
}

/** Whether a published version's effective time is still ahead of `now`. */
export function isScheduled(version: VersionSummary, now: Date): boolean {
  return (
    version.status === 'published' &&
    version.effective_at !== null &&
    new Date(version.effective_at).getTime() > now.getTime()
  );
}
